import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const retroPop: TemplateRegistration = {
  meta: {
    id: 'retro-pop',
    name: 'Retro Pop',
    category: 'Creative',
    description: '90s/Y2K retro pop art style with bold colors, thick borders, and nostalgic fun',
    previewImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
    themeEditable: false,
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(0, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Bungee', 'Inter']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="rp-reviews">
        <div class="container">
          <div class="sticker-badge rating-sticker">
            <div class="sticker-inner">
              <span class="sticker-stars">${renderStars(Math.round(rating))}</span>
              <span class="sticker-text">${rating.toFixed(1)} / 5 &bull; ${reviewCount} reviews</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="rp-promo">
        <div class="container">
          <div class="promo-banner">
            <div class="promo-zigzag"></div>
            <span class="promo-discount">${promoDiscount}</span>
            <h3>${promoTitle}</h3>
            <p>${promoDesc}</p>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="rp-section halftone-bg">
        <div class="container">
          <div class="section-header">
            <h2><span class="text-shadow-red">About Us</span></h2>
          </div>
          <div class="about-layout">
            <div class="about-text retro-card border-blue">
              <p>${safeAbout}</p>
            </div>
            ${galleryImages.length > 0 ? `
            <div class="about-image-wrap">
              <div class="retro-frame border-red">
                <img src="${escapeAttr(galleryImages[0])}" alt="${escapeAttr(safeName)}" loading="lazy">
                <div class="frame-corner frame-tl"></div>
                <div class="frame-corner frame-tr"></div>
                <div class="frame-corner frame-bl"></div>
                <div class="frame-corner frame-br"></div>
              </div>
            </div>` : ''}
          </div>
        </div>
      </section>`);
    }

    // --- Bold Divider ---
    sections.push(`<div class="bold-divider"><div class="divider-stripe stripe-red"></div><div class="divider-stripe stripe-blue"></div><div class="divider-stripe stripe-yellow"></div></div>`);

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map((s, i) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const icon = s.icon ? getIconSvg(s.icon) : getIconSvg('zap');
        const borderClass = `border-${['red', 'blue', 'yellow'][i % 3]}`;
        return `
          <div class="retro-card service-card ${borderClass}">
            <div class="service-icon">${icon}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<div class="price-sticker">${sanitizeText(s.price)}</div>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="rp-section">
        <div class="container">
          <div class="section-header">
            <h2><span class="text-shadow-blue">Our Services</span></h2>
          </div>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url, i) => {
        const borderClass = `border-${['red', 'blue', 'yellow'][i % 3]}`;
        return `<div class="gallery-item retro-frame ${borderClass}"><img src="${escapeAttr(url)}" alt="${escapeAttr(safeName)}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="rp-section">
        <div class="container">
          <div class="section-header">
            <h2><span class="text-shadow-yellow">Gallery</span></h2>
          </div>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const testimonialCards = data.testimonials.map((t, i) => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        const borderClass = `border-${['red', 'blue', 'yellow'][i % 3]}`;
        return `
          <div class="testimonial-card ${borderClass}">
            <p>&ldquo;${tContent}&rdquo;</p>
            ${t.rating ? `<div class="t-stars">${renderStars(t.rating)}</div>` : ''}
            <div class="t-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="rp-section halftone-bg">
        <div class="container">
          <div class="section-header">
            <h2><span class="text-shadow-red">Reviews</span></h2>
          </div>
          <div class="testimonials-grid">${testimonialCards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map((f, i) => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        const borderClass = `border-${['red', 'blue', 'yellow'][i % 3]}`;
        return `
          <div class="retro-card faq-item ${borderClass}">
            <h3>${q}</h3>
            <p>${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="rp-section">
        <div class="container-narrow">
          <div class="section-header">
            <h2><span class="text-shadow-blue">FAQ</span></h2>
          </div>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map((p, i) => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        const borderClass = `border-${['red', 'blue', 'yellow'][i % 3]}`;
        return `
          <div class="retro-card price-row ${borderClass}">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <div class="price-sticker">${pPrice}</div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="rp-section">
        <div class="container-narrow">
          <div class="section-header">
            <h2><span class="text-shadow-yellow">Pricing</span></h2>
          </div>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="rp-section">
        <div class="container-narrow">
          <div class="section-header">
            <h2><span class="text-shadow-red">Hours</span></h2>
          </div>
          <div class="retro-card border-blue">
            <ul class="hours-list">${hoursList}</ul>
          </div>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="rp-section halftone-bg">
        <div class="container">
          <div class="section-header">
            <h2><span class="text-shadow-blue">Contact</span></h2>
          </div>
          <div class="contact-split">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<a href="tel:${escapeAttr(phone)}" class="retro-chip">${getIconSvg('phone')} ${sanitizeText(phone)}</a>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="retro-chip">${getIconSvg('map-pin')} ${sanitizeText(address)}</div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="retro-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">Open in Maps &rarr;</a>` : ''}
            </div>
            <div class="contact-accent-bar"></div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <div class="form-row">
                <input type="text" placeholder="YOUR NAME" required>
                <input type="email" placeholder="YOUR EMAIL" required>
              </div>
              <textarea placeholder="YOUR MESSAGE..." rows="5" required></textarea>
              <button type="submit">SEND IT!</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #fefce8; --text: #18181b; --text-light: #3f3f46;
      --red: #dc2626; --blue: #2563eb; --yellow: #eab308; --black: #18181b;
      --border-w: 3px; --shadow-offset: 5px;
      --font-heading: 'Bungee', cursive; --font-body: 'Inter', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7; font-size: 16px; overflow-x: hidden; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 32px; }
    .container-narrow { max-width: 720px; margin: 0 auto; padding: 0 32px; }
    a { color: var(--blue); text-decoration: none; transition: color .2s; }
    a:hover { color: var(--red); }

    /* RETRO UTILITIES */
    .retro-card { background: #fff; border: var(--border-w) solid var(--black); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--black); padding: 28px; border-radius: 4px; }
    .border-red { border-color: var(--red); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--red); }
    .border-blue { border-color: var(--blue); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--blue); }
    .border-yellow { border-color: var(--yellow); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--yellow); }
    .text-shadow-red { text-shadow: 4px 4px 0 var(--red); }
    .text-shadow-blue { text-shadow: 4px 4px 0 var(--blue); }
    .text-shadow-yellow { text-shadow: 4px 4px 0 var(--yellow); }

    .halftone-bg { background-image: radial-gradient(circle, rgba(0,0,0,0.06) 1px, transparent 1px); background-size: 12px 12px; }

    /* NAV */
    .navbar { padding: 0; background: var(--black); position: sticky; top: 0; z-index: 100; }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; max-width: 1100px; margin: 0 auto; padding: 14px 32px; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.1rem; color: var(--yellow); text-decoration: none; letter-spacing: 1px; }
    .nav-brand:hover { color: var(--red); }
    .nav-links { display: flex; gap: 20px; list-style: none; }
    .nav-links a { color: #fff; font-size: .82rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; text-decoration: none; transition: color .2s; }
    .nav-links a:hover { color: var(--yellow); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; padding: 4px; }
    .nav-toggle span { display: block; width: 24px; height: 3px; background: #fff; margin: 4px 0; border-radius: 0; }

    /* HERO - dual CTA panels */
    .hero { padding: 80px 0 60px; position: relative; overflow: hidden; }
    .hero-inner { max-width: 1100px; margin: 0 auto; padding: 0 32px; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2rem, 5vw, 3.2rem); line-height: 1.2; margin-bottom: 24px; color: var(--black); text-shadow: 4px 4px 0 var(--red); text-align: center; }
    .hero-sub { color: var(--text-light); font-size: 1.05rem; margin-bottom: 32px; line-height: 1.8; max-width: 600px; text-align: center; margin-left: auto; margin-right: auto; }
    .hero-panels { display: grid; grid-template-columns: 1fr 6px 1fr; gap: 0; align-items: stretch; }
    .hero-panel { background: #fff; border: var(--border-w) solid var(--black); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--black); padding: 40px 32px; display: flex; flex-direction: column; align-items: center; text-align: center; justify-content: center; }
    .hero-panel h3 { font-family: var(--font-heading); font-size: 1rem; margin-bottom: 12px; text-transform: uppercase; }
    .hero-panel p { color: var(--text-light); font-size: .92rem; margin-bottom: 20px; line-height: 1.7; }
    .hero-accent-stripe { background: repeating-linear-gradient(180deg, var(--red) 0, var(--red) 12px, var(--blue) 12px, var(--blue) 24px, var(--yellow) 24px, var(--yellow) 36px); }
    .hero-cta { display: inline-block; padding: 14px 36px; background: var(--red); color: #fff; font-family: var(--font-heading); font-size: .9rem; letter-spacing: 1px; border: var(--border-w) solid var(--black); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--black); transition: transform .1s, box-shadow .1s; text-decoration: none; }
    .hero-cta:hover { transform: translate(2px, 2px); box-shadow: 3px 3px 0 var(--black); color: #fff; }
    .hero-cta-alt { display: inline-block; padding: 14px 36px; background: var(--blue); color: #fff; font-family: var(--font-heading); font-size: .9rem; letter-spacing: 1px; border: var(--border-w) solid var(--black); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--black); transition: transform .1s, box-shadow .1s; text-decoration: none; }
    .hero-cta-alt:hover { transform: translate(2px, 2px); box-shadow: 3px 3px 0 var(--black); color: #fff; }
    .retro-frame { position: relative; border: var(--border-w) solid var(--black); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--black); overflow: hidden; }
    .retro-frame img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .frame-corner { position: absolute; width: 20px; height: 20px; }
    .frame-tl { top: -2px; left: -2px; border-top: 4px solid var(--yellow); border-left: 4px solid var(--yellow); }
    .frame-tr { top: -2px; right: -2px; border-top: 4px solid var(--blue); border-right: 4px solid var(--blue); }
    .frame-bl { bottom: -2px; left: -2px; border-bottom: 4px solid var(--blue); border-left: 4px solid var(--blue); }
    .frame-br { bottom: -2px; right: -2px; border-bottom: 4px solid var(--yellow); border-right: 4px solid var(--yellow); }
    .sticker-badge { display: inline-flex; flex-direction: column; align-items: center; background: var(--yellow); border: var(--border-w) solid var(--black); border-radius: 50%; padding: 16px; box-shadow: 3px 3px 0 var(--black); transform: rotate(8deg); }
    .rating-sticker { border-radius: 8px; padding: 16px 24px; transform: rotate(-2deg); }
    .sticker-inner { text-align: center; }
    .sticker-stars .star { font-size: 1rem; }
    .sticker-stars .star.filled { color: var(--black); }
    .sticker-text { display: block; font-size: .8rem; font-weight: 700; margin-top: 4px; color: var(--black); }

    /* BOLD DIVIDER */
    .bold-divider { display: flex; height: 8px; }
    .divider-stripe { flex: 1; }
    .stripe-red { background: var(--red); }
    .stripe-blue { background: var(--blue); }
    .stripe-yellow { background: var(--yellow); }

    /* SECTIONS */
    .rp-section { padding: 72px 0; }
    .section-header { text-align: center; margin-bottom: 48px; }
    .rp-section h2 { font-family: var(--font-heading); font-size: clamp(1.4rem, 3vw, 1.8rem); color: var(--black); text-transform: uppercase; letter-spacing: 1px; }

    /* ABOUT */
    .about-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 36px; align-items: center; }
    .about-text p { color: var(--text-light); line-height: 1.9; }
    .about-image-wrap .retro-frame { height: 320px; }

    /* SERVICES - icon-top cards (icon centered above) */
    .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .service-card { transition: transform .1s, box-shadow .1s; text-align: center; }
    .service-card:hover { transform: translate(2px, 2px); box-shadow: 3px 3px 0 var(--black); }
    .service-icon { margin-bottom: 16px; color: var(--text); display: flex; justify-content: center; }
    .service-icon .icon-char { font-size: 32px; line-height: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: .95rem; margin-bottom: 8px; text-transform: uppercase; }
    .service-card p { color: var(--text-light); font-size: .9rem; line-height: 1.7; margin-bottom: 12px; }
    .price-sticker { display: inline-block; background: var(--yellow); border: 2px solid var(--black); padding: 4px 14px; font-family: var(--font-heading); font-size: .85rem; transform: rotate(-2deg); box-shadow: 2px 2px 0 var(--black); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
    .gallery-grid .retro-frame { height: 220px; }
    .gallery-grid .retro-frame img { transition: transform .3s; }
    .gallery-grid .retro-frame:hover img { transform: scale(1.05); }

    /* TESTIMONIALS - 3-col cards */
    .testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .testimonial-card { background: #fff; border: var(--border-w) solid var(--black); padding: 28px; box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--black); }
    .testimonial-card.border-red { border-color: var(--red); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--red); }
    .testimonial-card.border-blue { border-color: var(--blue); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--blue); }
    .testimonial-card.border-yellow { border-color: var(--yellow); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--yellow); }
    .testimonial-card p { color: var(--text-light); font-size: .92rem; line-height: 1.7; font-style: italic; margin-bottom: 16px; }
    .testimonial-card .t-stars { margin-top: 8px; }
    .star { color: #d4d4d8; font-size: .9rem; }
    .star.filled { color: var(--yellow); }
    .testimonial-card .t-author { margin-top: 12px; }
    .testimonial-card .t-author strong { font-family: var(--font-heading); font-size: .8rem; display: block; text-transform: uppercase; }
    .testimonial-card .t-author span { font-size: .8rem; color: var(--text-light); }

    /* GOOGLE REVIEWS */
    .rp-reviews { padding: 32px 0; text-align: center; }

    /* PROMO */
    .rp-promo { padding: 32px 0; }
    .promo-banner { background: var(--blue); color: #fff; border: var(--border-w) solid var(--black); box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--black); padding: 32px; text-align: center; position: relative; overflow: hidden; }
    .promo-zigzag { position: absolute; top: 0; left: 0; right: 0; height: 8px; background: repeating-linear-gradient(90deg, var(--yellow) 0, var(--yellow) 16px, var(--red) 16px, var(--red) 32px); }
    .promo-discount { display: inline-block; background: var(--yellow); color: var(--black); font-family: var(--font-heading); padding: 6px 18px; border: 2px solid var(--black); margin-bottom: 12px; transform: rotate(-2deg); font-size: .9rem; box-shadow: 2px 2px 0 var(--black); }
    .promo-banner h3 { font-family: var(--font-heading); font-size: 1.15rem; margin-bottom: 8px; text-transform: uppercase; }
    .promo-banner p { font-size: .92rem; opacity: .9; }

    /* FAQ */
    .faq-list { display: flex; flex-direction: column; gap: 16px; }
    .faq-item h3 { font-family: var(--font-heading); font-size: .88rem; text-transform: uppercase; margin-bottom: 8px; }
    .faq-item p { color: var(--text-light); font-size: .9rem; line-height: 1.7; }

    /* PRICE LIST */
    .price-list { display: flex; flex-direction: column; gap: 16px; }
    .price-row { display: flex; justify-content: space-between; align-items: center; }
    .price-info h3 { font-family: var(--font-heading); font-size: .88rem; text-transform: uppercase; margin-bottom: 4px; }
    .price-info p { color: var(--text-light); font-size: .85rem; }

    /* HOURS */
    .hours-list { list-style: none; }
    .hours-list li { padding: 12px 0; border-bottom: 2px dashed var(--text); color: var(--text-light); font-size: .92rem; font-weight: 600; }
    .hours-list li:last-child { border-bottom: none; }

    /* CONTACT - split with accent line */
    .contact-split { display: grid; grid-template-columns: 1fr 6px 1.2fr; gap: 0; align-items: start; }
    .contact-accent-bar { background: repeating-linear-gradient(180deg, var(--red) 0, var(--red) 12px, var(--blue) 12px, var(--blue) 24px, var(--yellow) 24px, var(--yellow) 36px); min-height: 100%; }
    .contact-info { display: flex; flex-direction: column; gap: 14px; padding: 28px; }
    .retro-chip { display: inline-flex; align-items: center; gap: 8px; background: #fff; border: 2px solid var(--black); padding: 10px 18px; font-size: .88rem; font-weight: 700; color: var(--text); text-decoration: none; box-shadow: 3px 3px 0 var(--black); transition: transform .1s, box-shadow .1s; }
    .retro-chip:hover { transform: translate(1px, 1px); box-shadow: 2px 2px 0 var(--black); color: var(--text); }
    .retro-chip .icon-char { font-size: 18px; line-height: 1; }
    .retro-link { font-weight: 700; font-size: .85rem; text-transform: uppercase; }
    .contact-form { display: flex; flex-direction: column; gap: 14px; padding: 28px; }
    .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
    .contact-form input, .contact-form textarea { background: var(--bg); border: 2px solid var(--black); padding: 14px 16px; font-family: var(--font-body); font-size: .9rem; font-weight: 600; color: var(--text); outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--red); box-shadow: 3px 3px 0 var(--red); }
    .contact-form input::placeholder, .contact-form textarea::placeholder { color: #a1a1aa; text-transform: uppercase; font-size: .8rem; letter-spacing: 1px; }
    .contact-form button { align-self: flex-start; padding: 14px 36px; background: var(--red); border: var(--border-w) solid var(--black); color: #fff; font-family: var(--font-heading); font-size: .9rem; letter-spacing: 1px; cursor: pointer; box-shadow: var(--shadow-offset) var(--shadow-offset) 0 var(--black); transition: transform .1s, box-shadow .1s; }
    .contact-form button:hover { transform: translate(2px, 2px); box-shadow: 3px 3px 0 var(--black); }

    /* FOOTER */
    .footer { background: var(--black); color: #fff; padding: 32px 0; text-align: center; }
    .footer p { font-size: .82rem; color: #a1a1aa; }
    .footer-brand { font-family: var(--font-heading); color: var(--yellow); font-size: .9rem; display: block; margin-bottom: 8px; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; }
      .nav-links.show { display: flex; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: var(--black); padding: 16px 32px; gap: 12px; border-bottom: 3px solid var(--yellow); }
      .nav-toggle { display: block; }
      .hero { padding: 80px 0 40px; }
      .hero-panels { grid-template-columns: 1fr; gap: 0; }
      .hero-accent-stripe { height: 6px; width: 100%; }
      .about-layout { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .rp-section { padding: 56px 0; }
      .form-row { grid-template-columns: 1fr; }
    }
    @media (max-width: 480px) {
      .services-grid { grid-template-columns: 1fr; }
      .testimonials-grid { grid-template-columns: 1fr; }
      .contact-split { grid-template-columns: 1fr; }
      .contact-accent-bar { height: 6px; width: 100%; }
      .gallery-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; align-items: flex-start; gap: 12px; }
      .hero h1 { text-shadow: 3px 3px 0 var(--red); }
      .rp-section h2 span { text-shadow: 3px 3px 0 currentColor; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-inner">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      <div class="hero-panels">
        <div class="hero-panel">
          <h3>Get Started</h3>
          <p>Ready to work with us? Reach out today.</p>
          ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">CONTACT US</a>` : ''}
        </div>
        <div class="hero-accent-stripe"></div>
        <div class="hero-panel">
          <h3>Our Work</h3>
          <p>See what we offer and how we can help.</p>
          ${enabledFeatures.has('services-list') && data.services.length > 0 ? `<a href="#services" class="hero-cta-alt">VIEW SERVICES</a>` : ''}
        </div>
      </div>
    </div>
  </section>

  <div class="bold-divider"><div class="divider-stripe stripe-red"></div><div class="divider-stripe stripe-blue"></div><div class="divider-stripe stripe-yellow"></div></div>

  ${sections.join('\n')}

  <div class="bold-divider"><div class="divider-stripe stripe-yellow"></div><div class="divider-stripe stripe-red"></div><div class="divider-stripe stripe-blue"></div></div>

  <footer class="footer">
    <div class="container">
      <span class="footer-brand">${safeName}</span>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
